import React from 'react';
import {
    AutocompleteInput,
    Datagrid,
    DateInput,
    Filter,
    FunctionField,
    List,
    NumberField,
    ReferenceInput,
    SelectInput,
    TextField,
} from 'react-admin';
import { Alert } from '@mui/material';

const KINDS = [
    { id: 'voice', name: 'Voz' },
    { id: 'text', name: 'Texto' },
];

/**
 * Excedente facturable entre dos fechas (86cbaa21u, respuesta a Q2).
 *
 * **El filtro por rango es el nucleo de esta pantalla, no un adorno.** El flujo real de
 * Administracion es: facturar hasta la fecha de la factura y, en la siguiente, volver a filtrar
 * desde ahi. Por eso el back devuelve `E(to) - E(from)` y no un acumulado: asi no hace falta que
 * el ATS guarde "lo ya facturado", basta la fecha de la ultima factura, que Admin ya tiene.
 *
 * `from` es **exclusivo**: se factura lo que entra despues de la ultima factura, no otra vez el
 * dia de esa factura.
 *
 * Incluye el consumo **sin bono**, que es igual de facturable: no existe el consumo gratis.
 */
const Filters = (props) => (
    <Filter {...props}>
        <ReferenceInput
            source="client_id"
            reference="clients"
            alwaysOn
            label="Cliente"
            sort={{ field: 'name', order: 'ASC' }}
            perPage={1000}
        >
            <AutocompleteInput
                source="name"
                optionText="name"
                filterToQuery={(searchText) => ({ name: searchText })}
            />
        </ReferenceInput>
        <SelectInput source="kind" label="Tipo" choices={KINDS} alwaysOn />
        <DateInput source="from" label="Desde (exclusivo)" alwaysOn />
        <DateInput source="to" label="Hasta (inclusive)" alwaysOn />
    </Filter>
);

/**
 * El aviso va FUERA del `List` y `empty` esta desactivado a proposito.
 *
 * En react-admin el componente `empty` sustituye la vista entera cuando no hay datos ni filtros
 * aplicados — **barra de filtros incluida**. Aqui era peor que en bonos: esta pantalla no
 * devuelve nada hasta que estan los cuatro filtros, asi que el aviso tapaba permanentemente los
 * mismos filtros que pedia rellenar.
 */
const Hint = () => (
    <Alert severity="info" sx={{ mt: 2 }}>
        Elige cliente, tipo y las dos fechas. Se devuelve lo facturable en ese tramo —
        <strong> «desde» es exclusivo</strong>, así que puedes encadenar facturas sin cobrar dos veces
        el mismo día.
    </Alert>
);

const AiAgentOverageList = () => (
    <>
        <Hint />
        <List filters={<Filters />} empty={false} exporter={false} pagination={false}>
        <Datagrid bulkActionButtons={false} rowClick={false}>
            <TextField source="kind" label="Tipo" />
            <TextField source="unit" label="Unidad" />
            <TextField source="from" label="Desde" />
            <TextField source="to" label="Hasta" />
            <NumberField source="included_units" label="Incluido en el bono" />
            {/* Sin bono, todo el consumo del tramo es facturable. */}
            <FunctionField
                label="Tiene bono"
                render={(record) => (record?.has_pack ? 'Sí' : 'No — consumo sin bono')}
            />
            <NumberField source="overage_at_from" label="Excedente en «desde»" />
            <NumberField source="overage_at_to" label="Excedente en «hasta»" />
            {/* Esto es lo que se factura: la diferencia, no el acumulado. */}
            <FunctionField
                label="A FACTURAR"
                render={(record) =>
                    `${(record?.billable_units ?? 0).toLocaleString()} ${record?.unit ?? ''}`
                }
            />
        </Datagrid>
        </List>
    </>
);

export default AiAgentOverageList;
